import React, { memo, useState } from 'react';

import { cssMap as unboundedCssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import PageIcon from '@atlaskit/icon/core/page';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import IconTile from '@atlaskit/icon/icon-tile';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { Card as SmartCard } from '@atlaskit/smart-card/card/lazy';
import { ActionName, ElementName, SmartLinkSize } from '@atlaskit/smart-card/enums';
import { MetadataBlock } from '@atlaskit/smart-card/metadata-block';
import { TitleBlock } from '@atlaskit/smart-card/title-block';
import { token } from '@atlaskit/tokens';
import { cleanExcerpt } from '@atlassian/search-common/search-result-utils';

import { useConfluenceAiSnippet } from '../../../api/useConfluenceAiSnippet/useConfluenceAiSnippet';
import { messages } from './messages';

const unboundedStyles = unboundedCssMap({
	titleBlock: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-nested-selectors
		'a[data-smart-element="Title"]': {
			font: token('font.heading.small'),
		},
	},
	metadataContainer: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="CommentCount"]': {
			verticalAlign: 'text-top',
			marginLeft: token('space.050'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="ReactCount"]': {
			verticalAlign: 'text-top',
			marginLeft: token('space.050'),
		},
	},
});

const styles = cssMap({
	titleBlock: {
		width: '100%',
	},
	titleIconContainer: {
		display: 'flex',
		alignSelf: 'flex-start',
		paddingTop: token('space.100'),
	},
	metadataContainer: {
		marginTop: token('space.negative.050'),
	},
	productNameContainer: {
		display: 'flex',
		alignItems: 'baseline',
	},
	snippetContainer: {
		marginTop: token('space.050'),
	},
});

// using vanilla css because primitives don't allow assinging 'color.border' to color which linking platform uses as a separator for metadata
const separatorStyle = {
	display: 'inline',
	color: token('color.border'),
	paddingLeft: token('space.050'),
	paddingRight: token('space.050'),
	font: token('font.body.small'),
};

export type ConfluenceFlexibleSmartCardProps = {
	url: string;
	cloudId: string;
	contentId: string;
	contentType: string;
	description: string;
	areLLMPoweredFeaturesEnabled: boolean;
	workspaceId: string;
	product: string;
	onLoad: () => void;
	onClick: ({
		event,
		resourceUrl,
		location,
	}: {
		event: React.MouseEvent | React.KeyboardEvent;
		resourceUrl: string;
		location: string;
	}) => void;
	searchPageAnalyticsCallbacks: {
		onResultClicked: () => void;
		onResultContextMenu: () => void;
		onCopyLinkButtonClicked: () => void;
	};
	onActionButtonClicked: (action: string) => void;
};

const ConfluenceFlexibleSmartCardUnmemoized = ({
	url,
	cloudId,
	contentId,
	contentType,
	description,
	areLLMPoweredFeaturesEnabled,
	onLoad,
	onClick,
	onActionButtonClicked,
	searchPageAnalyticsCallbacks,
	workspaceId,
	product,
}: ConfluenceFlexibleSmartCardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isReady, setIsReady] = useState(false);
	const {
		aiSnippet,
		loading: aiSnippetLoading,
		error: aiSnippetError,
	} = useConfluenceAiSnippet({
		cloudId,
		id: contentId,
		workspaceId,
		product,
	});

	const fallbackContent = (
		<Text maxLines={2} testId="fallback-description">
			{formatMessage(messages.fallback_description, {
				fallbackDescription: cleanExcerpt(description).trim(),
			})}
		</Text>
	);

	return (
		<>
			{/* We're using css to hide/show the loading state as a workaround because SmartCard component does not support loading states */}
			{/* PS: the smart card still has to be in the dom for it to work, do not use conditional logic to render it */}
			<div
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					transition: 'visibility 0.3s ease-in-out, opacity 0.3s ease-in-out',
					visibility: isReady ? 'hidden' : 'visible',
					opacity: isReady ? 0 : 1,
					pointerEvents: isReady ? 'none' : 'auto',
					height: isReady ? 0 : 'auto',
					width: isReady ? 0 : '100%',
				}}
			>
				<Stack space="space.150">
					<Inline alignBlock="center" space="space.150">
						<Box xcss={styles.titleIconContainer}>
							<Skeleton height={32} width={32} isShimmering testId="skeleton" />
						</Box>
						<Stack xcss={styles.titleBlock} space="space.100">
							<Skeleton height={32} width={200} isShimmering testId="skeleton" />
							<Skeleton height={20} width={200} isShimmering testId="skeleton" />
						</Stack>
					</Inline>
					<Box>
						<Skeleton height={20} width="100%" isShimmering testId="skeleton" />
					</Box>
				</Stack>
			</div>
			<div
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					transition: 'visibility 0.3s ease-in-out, opacity 0.3s ease-in-out',
					visibility: isReady ? 'visible' : 'hidden',
					opacity: isReady ? 1 : 0,
					pointerEvents: isReady ? 'auto' : 'none',
					height: isReady ? 'auto' : 0,
					width: isReady ? '100%' : 0,
				}}
				onContextMenu={searchPageAnalyticsCallbacks.onResultContextMenu}
			>
				<SmartCard
					url={url}
					ui={{
						removeBlockRestriction: true,
						hidePadding: true,
						hideElevation: true,
						hideBackground: true,
						clickableContainer: true,
					}}
					appearance="block"
					onClick={(e) => {
						onClick({ event: e, resourceUrl: url, location: 'page-link' });
						searchPageAnalyticsCallbacks.onResultClicked();
					}}
					onResolve={() => {
						setIsReady(true);
						onLoad();
					}}
					onError={() => setIsReady(true)}
					testId="smart-card"
				>
					<Inline alignBlock="center" space="space.150">
						<Box xcss={styles.titleIconContainer}>
							<IconTile
								icon={contentType === 'blogpost' ? QuotationMarkIcon : PageIcon}
								label={formatMessage(messages.page_icon_label)}
								appearance="blue"
								size="medium"
							/>
						</Box>
						<Box xcss={cx(styles.titleBlock, unboundedStyles.titleBlock)}>
							<TitleBlock
								hideIcon
								actions={[
									{
										name: ActionName.CopyLinkAction,
										onClick: () => {
											onActionButtonClicked(ActionName.CopyLinkAction);
											searchPageAnalyticsCallbacks.onCopyLinkButtonClicked();
										},
										hideContent: true,
									},
									{
										name: ActionName.PreviewAction,
										onClick: () => onActionButtonClicked(ActionName.PreviewAction),
										hideContent: true,
									},
								]}
								maxLines={1}
							/>
							<Inline
								alignBlock="baseline"
								xcss={cx(styles.metadataContainer, unboundedStyles.metadataContainer)}
							>
								<Box xcss={cx(styles.productNameContainer)}>
									<Text size="small" color="color.text.subtle">
										{formatMessage(messages.confluence_product_name)}
									</Text>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
									<div style={separatorStyle}>•</div>
								</Box>
								<MetadataBlock
									primary={[
										{ name: ElementName.CreatedBy },
										{ name: ElementName.ModifiedOn },
										{ name: ElementName.ViewCount },
										{ name: ElementName.CommentCount },
										{ name: ElementName.ReactCount },
										{ name: ElementName.VoteCount },
									]}
									size={SmartLinkSize.Small}
									maxLines={1}
								/>
							</Inline>
						</Box>
					</Inline>
					<Box xcss={styles.snippetContainer}>
						{areLLMPoweredFeaturesEnabled ? (
							aiSnippetLoading ? (
								<Skeleton height={20} width="90%" isShimmering testId="skeleton" />
							) : aiSnippetError ? (
								fallbackContent
							) : aiSnippet ? (
								<Text maxLines={2}>{aiSnippet}</Text>
							) : (
								fallbackContent
							)
						) : (
							fallbackContent
						)}
					</Box>
				</SmartCard>
			</div>
		</>
	);
};

export const ConfluenceFlexibleSmartCard: React.MemoExoticComponent<
	({
		url,
		cloudId,
		contentId,
		contentType,
		description,
		areLLMPoweredFeaturesEnabled,
		onLoad,
		onClick,
		onActionButtonClicked,
		searchPageAnalyticsCallbacks,
		workspaceId,
		product,
	}: ConfluenceFlexibleSmartCardProps) => React.JSX.Element
> = memo(ConfluenceFlexibleSmartCardUnmemoized);
